import React, { useState, useMemo, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import { CookieContext, type CookieConsent } from "./CookieContext";
import { initGA, disableGA, trackPageView } from "../utils/analytics";

// Shared cookie options: SameSite=Lax prevents CSRF, Secure ensures HTTPS-only in production
const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  expires: 365,
  sameSite: "Lax",
  secure: window.location.protocol === "https:",
};

export const CookieProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(() => {
    const savedConsent = Cookies.get("cookie_consent");
    if (savedConsent) {
      try {
        return JSON.parse(savedConsent);
      } catch {
        return null;
      }
    }
    return null;
  });

  const [bannerOpen, setBannerOpen] = useState(() => !Cookies.get("cookie_consent"));
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Enable or disable GA4 based on analytics consent
  useEffect(() => {
    if (consent?.analytics) {
      initGA();
      // Fire a pageview for the current page in case consent was granted mid-session
      trackPageView(window.location.pathname + window.location.search);
    } else if (consent && !consent.analytics) {
      disableGA();
    }
  }, [consent]);

  const saveConsent = useCallback((newConsent: CookieConsent) => {
    setConsent(newConsent);
    Cookies.set("cookie_consent", JSON.stringify(newConsent), COOKIE_OPTIONS);
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      saveConsent,
      bannerOpen,
      setBannerOpen,
      settingsOpen,
      setSettingsOpen,
    }),
    [consent, saveConsent, bannerOpen, settingsOpen]
  );

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
};
